import axios, { AxiosError, AxiosRequestConfig } from "axios";

/**
 * CloudStore API 控制台的 axios 实例。
 *
 * - baseURL 默认 /console（见 API 文档），可用环境变量覆盖为完整域名。
 * - 自动附带 JWT（登录后写入 localStorage 的 token，登录流程后续再做）。
 * - 统一解析后端 { code, message } 错误格式，reject 出可直接展示的 message。
 */

/** 后端统一错误响应体 */
export interface ApiErrorBody {
  code: string | number;
  message: string;
}

/** 抛给调用方的标准化错误 */
export class ApiError extends Error {
  code: string | number;
  status?: number;
  constructor(message: string, code: string | number, status?: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

const TOKEN_STORAGE_KEY = "cloudstore_console_token";

/** 401 未认证时广播的事件名；AuthContext 监听后登出并弹登录框 */
export const UNAUTHORIZED_EVENT = "cloudstore:unauthorized";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    /* ignore */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

const request = axios.create({
  baseURL: process.env.REACT_APP_CONSOLE_API_BASE || "/console",
  timeout: 20000,
});

// 请求拦截：附带 JWT
request.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截：直接返回 data，并把错误标准化为 ApiError
request.interceptors.response.use(
  (response) => response.data,
  (error: AxiosError<ApiErrorBody>) => {
    const status = error.response?.status;
    const body = error.response?.data;
    const message =
      body?.message ||
      error.message ||
      "Request failed. Please try again later.";
    const code = body?.code ?? status ?? "GEN_UNKNOWN";

    // 401 未认证：清除失效凭证并广播事件，由 AuthContext 登出、弹出登录框
    if (status === 401) {
      clearToken();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
      }
    }

    return Promise.reject(new ApiError(message, code, status));
  }
);

/** 泛型 GET，返回已解析的响应体 */
export function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return request.get(url, config) as unknown as Promise<T>;
}

/** 泛型 POST */
export function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  return request.post(url, data, config) as unknown as Promise<T>;
}

export default request;
